import type ClientItem from 'owa-mail-store/lib/store/schema/ClientItem';
import { getStore as getMailStore } from 'owa-mail-store/lib/store/Store';
import getLastestNonDraftItemId from 'owa-mail-reading-pane-store-conversation/lib/utils/getLastestNonDraftItemId';
import { isReadingPaneStackedView } from 'owa-mail-organization-settings/lib/utils/readingPaneStackedViewUtils';
import { getListViewState, getStore as getListViewStore } from 'owa-mail-list-store';
import { openChatPane, type OpenChatPaneOptions } from 'owa-copilot-dab/lib/utils/openChatPane';
import { openAgentChatPane } from 'owa-copilot-dab/lib/utils/openAgentChatPane';
import {
    MessageAnnotationSource,
    QueryAnnotationType,
    type QueryAnnotation,
} from '@1js/sydney-types';
import getConversationReadingPaneViewState from 'owa-mail-reading-pane-store-conversation/lib/utils/getConversationReadingPaneViewState';
import getItemReadingPaneViewState from 'owa-mail-reading-pane-store/lib/utils/getItemReadingPaneViewState';
import type { AgentType, OpenBizChatSidePaneParams } from 'owa-bizchat-types';
import type { MailboxInfo } from 'owa-client-types';
import { getMailboxInformationForAnnotation } from 'owa-bizchat-account-utils';
import { isSummaryForConversationsEnabled } from 'owa-bizchat-shared';
import { getPendingContext } from 'owa-bizchat-shared/lib/utils/PostMessageToFrameHandler';
import isPopout from 'owa-popout-v2/lib/utils/isPopout';
import { isNonExecutableCmmId } from 'owa-copilot-dab/lib/utils/isNonExecutableCmmId';
import { mapContentItemsToQueryAnnotations } from './mapContentItemsToQueryAnnotations';
import { logCoreUsage } from 'owa-analytics';
import { resolveSummarizeAnnotationIdAndType } from 'owa-mail-thread-summarization-content/lib/utils/resolveSummarizeAnnotationIdAndType';
import { reconcileQueryAnnotationIdTypes } from 'owa-mail-thread-summarization-content/lib/utils/reconcileQueryAnnotationIdTypes';
import { detectSummarizeAnnotationIdTypeMismatch } from 'owa-mail-thread-summarization-content/lib/telemetry/summarizeAnnotationIdTypeTelemetry';

/**
 * Specific variation of `executePrompt` that grabs the current selected mail's queryAnnotation to send
 * to Copilot Chat along with the rest of the prompt parameters.
 */
export const executePromptWithQueryAnnotation = (
    promptText: OpenChatPaneOptions['promptText'],
    /** Also known as "CMM ID" */
    formCode: OpenBizChatSidePaneParams['formCode'],
    targetWindow: Window,
    mailboxInfo: MailboxInfo,
    queryAnnotations?: QueryAnnotation[],
    agentType?: AgentType
): void => {
    const resolvedQueryAnnotations =
        queryAnnotations ?? getQueryAnnotations(targetWindow, mailboxInfo);
    // Caller-provided annotations and popout contentItems bypass the store-based resolver in
    // getQueryAnnotations, so reconcile id/type here too before handing off to BizChat.
    const reconciledQueryAnnotations = reconcileQueryAnnotationIdTypes(
        resolvedQueryAnnotations,
        mailboxInfo,
        'executePromptWithQueryAnnotation'
    );
    const shouldExecuteQuery = !isNonExecutableCmmId(formCode);

    if (agentType) {
        openAgentChatPane({
            promptText,
            targetWindow,
            agentType,
            shouldExecuteQuery,
            formCode,
            queryAnnotations: reconciledQueryAnnotations,
            mailboxInfo,
        });
        return;
    }

    openChatPane({
        params: {
            formCode,
            queryAnnotations: reconciledQueryAnnotations,
            shouldExecuteQuery,
        },
        promptText,
        targetWindow,
        mailboxInfo,
    });
};

const getQueryAnnotations = (targetWindow: Window, mailboxInfo: MailboxInfo): QueryAnnotation[] => {
    // For popout windows, use getPendingContext (store-based path doesn't work in popouts)
    if (isPopout(targetWindow)) {
        const contextualChat = getPendingContext(mailboxInfo, targetWindow);
        if (contextualChat?.contentItems) {
            return mapContentItemsToQueryAnnotations(contextualChat.contentItems);
        }
        if (isSummaryForConversationsEnabled(mailboxInfo)) {
            logCoreUsage('ThreadSummarization_MissingQueryAnnotations', mailboxInfo, {
                source: 'executePromptWithQueryAnnotation',
            });
        }
        return [];
    }

    // Main window: existing store-based logic
    const { selectedTableViewId } = getListViewState();
    const tableView = getListViewStore().tableViews.get(selectedTableViewId);
    const isConversationViewRP = isReadingPaneStackedView(tableView?.tableQuery.listViewType);

    const conversationReadingPaneViewState = getConversationReadingPaneViewState();
    const itemReadingPaneViewState = getItemReadingPaneViewState();

    // Do NOT fall back to getCurrentRenderedConversationId() — when SxS is active or a
    // SecondaryReadingPane item tab is selected, it returns the message itemId, and tagging
    // that with EmailConversation makes GCI reject the request with ErrorInvalidIdMalformed.
    const conversationId =
        isConversationViewRP && conversationReadingPaneViewState?.conversationId?.Id
            ? conversationReadingPaneViewState.conversationId.Id
            : undefined;
    const itemId = itemReadingPaneViewState?.itemId;

    // Prefer the conversation's latest non-draft item for clientItem (subject/etc.);
    // otherwise resolve directly from the open item reading pane so the annotation still
    // carries real context when the conversation id hasn't been populated yet.
    const clientItem = conversationId
        ? getClientItem(conversationId, true)
        : itemId
        ? getClientItem(itemId, false)
        : undefined;
    const mailboxInformation = getMailboxInformationForAnnotation(mailboxInfo);
    const isFlightEnabled = isSummaryForConversationsEnabled(mailboxInfo);

    // Shared resolver keeps the id/type decision identical across all summarize entry points.
    // conversationId is already taken only from the conversation reading-pane view state
    // above, so it is never a message item id from an SxS/secondary/projected window.
    const { id: annotationId, type: queryAnnotationType } = resolveSummarizeAnnotationIdAndType({
        isConversationOrganizedView: isConversationViewRP,
        isSummaryForConversationsEnabled: isFlightEnabled,
        conversationViewStateId: conversationId,
        latestConversationItemId: undefined,
        fallbackMessageId: clientItem?.ItemId?.Id || itemId,
    });

    // Log both failure modes so we can diagnose missing ids in the final annotation.
    if (!annotationId) {
        if (queryAnnotationType === QueryAnnotationType.EmailConversation) {
            logCoreUsage('ThreadSummarization_EmptyConversationIdForAnnotation', mailboxInfo, {
                source: 'executePromptWithQueryAnnotation',
            });
        } else {
            logCoreUsage('ThreadSummarization_EmptyMessageIdForAnnotation', mailboxInfo, {
                source: 'executePromptWithQueryAnnotation',
            });
        }
    } else if (isFlightEnabled && isConversationViewRP && !conversationId) {
        // We intended to send EmailConversation but the conversation id was missing — preserve
        // this signal even though we fell back to an item-id-based Message annotation.
        logCoreUsage('ThreadSummarization_EmptyConversationIdForAnnotation', mailboxInfo, {
            source: 'executePromptWithQueryAnnotation',
        });
    } else {
        const mismatch = detectSummarizeAnnotationIdTypeMismatch(queryAnnotationType, annotationId);
        if (mismatch) {
            logCoreUsage('ThreadSummarization_MismatchedAnnotationIdType', mailboxInfo, {
                source: 'executePromptWithQueryAnnotation',
                mismatch,
            });
        }
    }

    return [
        {
            id: annotationId,
            messageAnnotationSource: MessageAnnotationSource.ClientAnnotated,
            queryAnnotationType,
            text: clientItem?.Subject || '',
            messageAnnotationMetadata: {
                '@type': queryAnnotationType,
                mailboxInformation,
            },
        },
    ];
};

const getClientItem = (id: string, isConversationViewRP: boolean): ClientItem | undefined => {
    const { items, conversations } = getMailStore();
    const conversationId = conversations.get(id)?.conversationId.Id;
    const useLatestItemId = isConversationViewRP && conversationId;

    return items.get(useLatestItemId ? getLastestNonDraftItemId(conversationId) : id);
};
